import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setCallInProgress } from '../../features/channelSlice';
import ContactListView from './ContactListView';
import VideoCallView from './VideoCallView';
import { sendCaraStop } from '../../utils/caraBridge';

/**
 * InLiveKitCallView — renders VideoCallView for both voice and video calls.
 *
 * callType === 'video'  →  audioOnly=false  (avatar enabled, video tracks subscribed)
 * callType === 'voice'  →  audioOnly=true   (no avatar, audio-only)
 */
const InLiveKitCallView = () => {
    const dispatch = useDispatch();
    const { activeCallContact, callType, enableSoundEffects, channelConfig } = useSelector((state) => state.channel);
    const selectedBrandId = +(localStorage.getItem('selectedBrandId') ?? -1);
    const selectedConfigId = +(localStorage.getItem('selectedConfigId') ?? -1);

    const defaultContactName = channelConfig?.call?.contacts?.[0]?.name || 'Cara';
    const contactName = activeCallContact?.name || defaultContactName;
    const audioOnly = callType !== 'video';

    return (
        <VideoCallView
            applicationId={selectedConfigId}
            brandId={selectedBrandId}
            contactName={contactName}
            enableSoundEffects={enableSoundEffects}
            audioOnly={audioOnly}
            onCallEnd={() => dispatch(setCallInProgress(false))}
        />
    );
};

/**
 * VoiceCallView - Main component that decides which view to show
 *
 * Shows ContactListView when no call is active, InLiveKitCallView when call is in progress.
 * Both voice and video calls go through LiveKit via VideoCallView.
 */
export default function VoiceCallView({ isEnvironmentOffline = false }) {
    const { isCallInProgress, callInfo } = useSelector((state) => state.channel);
    const callType = useSelector((state) => state.channel.callType);
    const dispatch = useDispatch();
    const previousCallStateRef = React.useRef({
        isCallInProgress: false,
        callType: null,
    });

    // Effect to automatically set call in progress if callInfo is received
    useEffect(() => {
        if (callInfo) {
            dispatch(setCallInProgress(true));
        }
    }, [callInfo, dispatch]);

    useEffect(() => {
        const previousState = previousCallStateRef.current;

        if (previousState.isCallInProgress && !isCallInProgress && previousState.callType === 'video') {
            sendCaraStop();
        }

        previousCallStateRef.current = {
            isCallInProgress,
            callType,
        };
    }, [isCallInProgress, callType]);

    return isCallInProgress
        ? <InLiveKitCallView />
        : <ContactListView />;
}
